/**
 * Carry the vehicle selector's saved truck onto the order as line item properties.
 *
 * This is D2 from migration/vehicle-fitment.md, which Marcus answered on
 * 2026-08-17 and which was never built:
 *
 *   "The vehicle selector already knows the customer's truck. Carry that
 *    selection into the cart as line item properties, attached automatically at
 *    add-to-cart."
 *
 * sections/vehicle-selector.liquid already persists the chosen truck in
 * localStorage under `dethloff.garage`. Nothing has ever read it back into the
 * cart. So today Marcus learns the chassis only on the five products that ask
 * outright, and only when the buyer goes through the form — not on a permalink,
 * not on express checkout, and not on the other twenty products at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT ATTACHES NOTHING RATHER THAN ATTACHING SOMETHING WRONG.
 *
 * A saved Ford on a Ram skid plate order is worse than no answer: it is a
 * confident wrong answer, and Marcus reads these. So the garage is checked
 * against the product's OWN fitment metafields first, and one contradicted field
 * suppresses the whole attachment. See `decide()`.
 *
 * ⚠ NO IMPORTS, AND THAT IS LOAD-BEARING. The test imports this exact file by
 * reading its bytes and evaluating them as a module, which cannot resolve a
 * relative path or a bare specifier. Keep it self-contained.
 *
 * Tested by extensions/fitment-validation/src/garage.test.mjs.
 */

/** Where sections/vehicle-selector.liquid saves the chosen truck. */
export const GARAGE_KEY = 'dethloff.garage';

/**
 * The fitment axes, in the order they read on an order.
 *
 * These are metafield keys, and they are deliberately the SAME names the
 * selector saves and the same ones the product carries under `custom.*`. Three
 * vocabularies for one concept is how this project got a case-sensitivity bypass
 * into production; two is the minimum here and they are kept identical.
 */
export const FIELDS = ['year', 'make', 'model', 'body_style', 'engine', 'cab', 'bed_length', 'box_type'];

/** Fields that make up the human-readable summary, in order. */
const SUMMARY_FIELDS = ['year', 'make', 'model'];

const clean = (v) => String(v == null ? '' : v).trim();
const fold = (v) => clean(v).toLowerCase();

/**
 * Parse the garage blob. Never throws — a corrupt or absent value is "no truck".
 * @param {string|null|undefined} raw
 * @returns {Record<string, string>|null}
 */
export function parseGarage(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  return parsed;
}

/**
 * "2016 Ram 2500 · 6.4L HEMI" — what the customer sees on the cart line.
 *
 * Shown rather than hidden on purpose: it is the one place a buyer can notice
 * that the part is going against the wrong truck, and it costs a line.
 * @param {Record<string, string>} garage
 * @returns {string}
 */
export function summarise(garage) {
  const head = SUMMARY_FIELDS.map((k) => clean(garage[k])).filter(Boolean).join(' ');
  const engine = clean(garage.engine);
  if (head && engine) return `${head} · ${engine}`;
  return head || engine;
}

/**
 * Decide what to attach for one product.
 *
 * @param {Record<string, string>|null} garage  the saved truck
 * @param {Record<string, string[]>} fitment    the product's `custom.*` lists
 * @returns {{verdict: string, properties: Record<string, string>, field?: string}}
 *
 * Verdicts:
 *   attached      the truck is consistent with the product; properties returned
 *   no-garage     nothing saved, or nothing in it that names a truck
 *   mismatch      a field the product DOES declare contradicts the garage
 *   unverifiable  the product declares no fitment at all, so nothing can be
 *                 checked. Attaches nothing — this is what keeps a truck off a
 *                 gift card, and off any product Marcus has not classified yet.
 */
export function decide(garage, fitment) {
  if (!garage || typeof garage !== 'object') return { verdict: 'no-garage', properties: {} };

  const present = FIELDS.filter((k) => clean(garage[k]) !== '');
  const summary = summarise(garage);
  /* A garage holding only a cab and a bed does not name a truck. Treat it as
   * empty rather than attaching "Crew Cab" with nothing to attach it to. */
  if (!present.length || !summary) return { verdict: 'no-garage', properties: {} };

  const lists = fitment && typeof fitment === 'object' ? fitment : {};
  let checked = 0;

  for (const key of present) {
    const raw = lists[key];
    const allowed = Array.isArray(raw) ? raw.map(clean).filter(Boolean) : [];
    /* The product says nothing about this axis, so it cannot contradict it.
     * D1 in vehicle-fitment.md requires real parts to spell out every value they
     * fit, so an empty list here means "unclassified", not "fits nothing". */
    if (!allowed.length) continue;
    checked++;
    if (!allowed.some((v) => fold(v) === fold(garage[key]))) {
      return { verdict: 'mismatch', properties: {}, field: key };
    }
  }

  if (!checked) return { verdict: 'unverifiable', properties: {} };

  /** @type {Record<string, string>} */
  const properties = { Vehicle: summary };
  /* Underscore-prefixed properties are stored on the order but hidden from the
   * customer at checkout, and snippets/cart-products.liquid already filters them
   * out of the cart. So Marcus gets one field per axis to sort and count on,
   * and the buyer sees one readable line. */
  for (const key of present) properties[`_vehicle_${key}`] = clean(garage[key]);

  return { verdict: 'attached', properties };
}

/**
 * <garage-properties data-form-id="..."> — injects the hidden inputs.
 *
 * The inputs carry `form="<id>"` rather than relying on being descendants:
 * `new FormData(form)` builds its entry list from the form's OWNER relationship,
 * so a field associated by id is submitted from anywhere in the document. This
 * is the same basis snippets/order-questions.liquid works on, and it is what
 * lets assets/product-form.js pick these up on BOTH of its paths — the FormData
 * one and the queued JSON one, which re-reads `properties[...]` off the form.
 *
 * ⚠ `extends Base`, not `extends HTMLElement`. A class body is evaluated when the
 * module loads, so naming HTMLElement directly would throw in Node before a
 * single test ran — guarding only the `define()` below is not enough.
 */
const Base = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class GarageProperties extends Base {
  connectedCallback() {
    this.sync();
    this._onStorage = (event) => {
      if (!event || event.key === null || event.key === GARAGE_KEY) this.sync();
    };
    window.addEventListener('storage', this._onStorage);
  }

  disconnectedCallback() {
    if (this._onStorage) window.removeEventListener('storage', this._onStorage);
  }

  /** @returns {Record<string, string[]>} */
  readFitment() {
    const node = this.querySelector('script[type="application/json"][data-garage-fitment]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}') || {};
    } catch {
      return {};
    }
  }

  sync() {
    for (const stale of [...this.querySelectorAll('input[data-garage-input]')]) stale.remove();

    const formId = this.getAttribute('data-form-id');
    if (!formId) return;

    let raw = null;
    try {
      raw = localStorage.getItem(GARAGE_KEY);
    } catch {
      /* Private mode, or storage disabled. No truck, no attachment. */
    }

    const result = decide(parseGarage(raw), this.readFitment());
    /* Observable without a debugger, and the hook a fitment warning would use. */
    this.setAttribute('data-garage-verdict', result.verdict);
    if (result.field) this.setAttribute('data-garage-mismatch-field', result.field);
    else this.removeAttribute('data-garage-mismatch-field');

    for (const [name, value] of Object.entries(result.properties)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('form', formId);
      input.setAttribute('data-garage-input', '');
      input.name = `properties[${name}]`;
      input.value = value;
      this.appendChild(input);
    }
  }
}

/* Guarded so the test can import this file in Node, where there is no DOM. */
if (typeof customElements !== 'undefined' && !customElements.get('garage-properties')) {
  customElements.define('garage-properties', GarageProperties);
}

export { GarageProperties };
